import { useEffect, useRef, useSyncExternalStore } from "react";
import { useAPI } from "@huub-web/backend";
import type {
  VocabEntry,
  VocabResponse,
  EnrichedVocab,
  RalColorEntry,
} from "../context/types";

// ─── RAL hex lookup ─────────────────────────────────────────

const RAL_HEX_MAP: Record<string, string> = {
  "RAL 9003": "#F4F4F4",
  "RAL 9016": "#F7F7F2",
  "RAL 9010": "#F1ECE1",
  "RAL 9001": "#FDF4E3",
  "RAL 7003": "#6C7059",
  "RAL 7004": "#969992",
  "RAL 7001": "#8B9EA0",
  "RAL 7016": "#293133",
};

const FALLBACK_HEX = "#999999";

function enrichRalColors(entries: VocabEntry[]): RalColorEntry[] {
  return entries.map((entry) => ({
    ...entry,
    hex: RAL_HEX_MAP[entry.id] ?? FALLBACK_HEX,
  }));
}

// ─── Module-level store ─────────────────────────────────────

type UseVocabReturn = {
  vocab: EnrichedVocab | null;
  isLoading: boolean;
  error: Error | null;
};

let cache: VocabResponse | null = null;
let inFlight: Promise<VocabResponse> | null = null;
let vocabError: Error | null = null;
let fetchGeneration = 0;

const listeners = new Set<() => void>();

let lastSnapshot: UseVocabReturn = {
  vocab: null,
  isLoading: false,
  error: null,
};
let lastEnrichedVocab: EnrichedVocab | null = null;
let lastCacheRef: VocabResponse | null = null;

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getEnrichedVocab(): EnrichedVocab | null {
  if (cache === lastCacheRef) return lastEnrichedVocab;
  lastCacheRef = cache;
  lastEnrichedVocab = cache
    ? { ...cache, ral_colors: enrichRalColors(cache.ral_colors) }
    : null;
  return lastEnrichedVocab;
}

function getSnapshot(): UseVocabReturn {
  const vocab = getEnrichedVocab();
  const isLoading = cache === null && vocabError === null;
  const error = vocabError;

  if (
    vocab === lastSnapshot.vocab &&
    isLoading === lastSnapshot.isLoading &&
    error === lastSnapshot.error
  ) {
    return lastSnapshot;
  }

  lastSnapshot = { vocab, isLoading, error };
  return lastSnapshot;
}

function fetchVocab($get: (url: string) => Promise<{ data: unknown }>) {
  if (cache) return;
  if (vocabError) return;
  if (inFlight) return;

  const gen = fetchGeneration;

  inFlight = $get("/interior-designer/vocab")
    .then((response) => {
      if (gen !== fetchGeneration) return null as never;
      cache = response.data as VocabResponse;
      return cache;
    })
    .catch((err: unknown) => {
      if (gen !== fetchGeneration) return null as never;
      vocabError = err instanceof Error ? err : new Error(String(err));
      return null as never;
    })
    .finally(() => {
      if (gen === fetchGeneration) {
        inFlight = null;
      }
      notify();
    });

  notify();
}

// ─── Public API ─────────────────────────────────────────────

let storedGetRef: {
  current: (url: string) => Promise<{ data: unknown }>;
} | null = null;

export function useVocab(): UseVocabReturn {
  const { $get } = useAPI();
  const $getRef = useRef($get);
  $getRef.current = $get;
  storedGetRef = $getRef;

  const snapshot = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    fetchVocab($getRef.current);
  }, []);

  return snapshot;
}

export function refetchVocab() {
  fetchGeneration++;
  cache = null;
  inFlight = null;
  vocabError = null;
  lastCacheRef = null;
  lastEnrichedVocab = null;
  if (storedGetRef) {
    fetchVocab(storedGetRef.current);
  }
  notify();
}

export function __resetVocabCache() {
  fetchGeneration++;
  cache = null;
  inFlight = null;
  vocabError = null;
  listeners.clear();
  lastSnapshot = { vocab: null, isLoading: false, error: null };
  lastEnrichedVocab = null;
  lastCacheRef = null;
  storedGetRef = null;
}
